import React, { useState, useEffect, useCallback, useMemo } from 'react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import API, { financeAPI } from '../../services/api';
import {
  HiOutlineDocumentAdd, HiOutlinePlus, HiOutlineTrash,
  HiOutlineRefresh, HiOutlineX, HiOutlineDownload, HiOutlineEye,
  HiOutlineSearch, HiOutlineTruck, HiOutlineDocumentText, HiOutlineReceiptTax
} from 'react-icons/hi';
import toast from 'react-hot-toast';
import { useOrg } from '../../context/OrgContext';

const validateGSTIN = (gstin) => {
  if (!gstin) return { isValid: false, message: '' };
  const trimmed = gstin.trim().toUpperCase();
  if (trimmed.length === 0) return { isValid: false, message: '' };

  const stateCodes = {
    '01': 'Jammu & Kashmir', '02': 'Himachal Pradesh', '03': 'Punjab', '04': 'Chandigarh', '05': 'Uttarakhand',
    '06': 'Haryana', '07': 'Delhi', '08': 'Rajasthan', '09': 'Uttar Pradesh', '10': 'Bihar', '11': 'Sikkim',
    '12': 'Arunachal Pradesh', '13': 'Nagaland', '14': 'Manipur', '15': 'Mizoram', '16': 'Tripura',
    '17': 'Meghalaya', '18': 'Assam', '19': 'West Bengal', '20': 'Jharkhand', '21': 'Odisha', '22': 'Chhattisgarh',
    '23': 'Madhya Pradesh', '24': 'Gujarat', '27': 'Maharashtra', '29': 'Karnataka', '32': 'Kerala', '33': 'Tamil Nadu',
    '36': 'Telangana', '37': 'Andhra Pradesh'
  };

  if (trimmed.length < 15) {
    return { isValid: false, message: `Entering GSTIN (${trimmed.length}/15 chars)` };
  }

  const regex = /^[0-9]{2}[A-Z]{5}[0-9]{4}[A-Z]{1}[1-9A-Z]{1}Z[0-9A-Z]{1}$/;
  if (!regex.test(trimmed)) {
    return { isValid: false, message: '❌ Invalid GSTIN Structure (Format: 07AAAAA1111A1Z1)' };
  }

  const stateCode = trimmed.substring(0, 2);
  const stateName = stateCodes[stateCode] ? ` (${stateCodes[stateCode]})` : '';

  // Official Govt MOD36 Checksum Validation Algorithm
  const chars = '0123456789ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  let factor = 1;
  let sum = 0;

  for (let i = 0; i < 14; i++) {
    const codePoint = chars.indexOf(trimmed[i]);
    if (codePoint === -1) return { isValid: false, message: '❌ Invalid GSTIN Characters' };
    
    let addend = factor * codePoint;
    factor = factor === 1 ? 2 : 1;
    sum += Math.floor(addend / 36) + (addend % 36);
  }

  const remainder = sum % 36;
  const checkCodePoint = (36 - remainder) % 36;
  const expectedCheckChar = chars[checkCodePoint];

  if (trimmed[14] === expectedCheckChar) {
    return { isValid: true, message: `✅ Verified Authentic GSTIN${stateName}` };
  } else {
    return { isValid: false, message: `⚠️ GSTIN Checksum Failed (Expected ending digit '${expectedCheckChar}')` };
  }
};

const CHALLAN_PURPOSES = [
  'Supply of Goods',
  'Job Work / Processing',
  'Line Transfer / Inter-branch',
  'Exhibition / Demonstration',
  'Warranty Replacement',
  'Customer Approval / Trial',
  'Other Transportation'
];

export default function InvoiceGenerator({ isEmbedded = false }) {
  const { currencySymbol, formatCurrency } = useOrg();

  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeFilterTab, setActiveFilterTab] = useState('all'); // 'all', 'non_gst', 'challan', 'tax_invoice'

  // Document Type Mode Switcher: 'non_gst_bill' | 'delivery_challan' | 'tax_invoice'
  const [documentType, setDocumentType] = useState('non_gst_bill');

  // Form State
  const [clientName, setClientName] = useState('');
  const [clientAddress, setClientAddress] = useState('');
  const [clientGST, setClientGST] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState('');

  // Delivery Challan Specific Fields
  const [vehicleNumber, setVehicleNumber] = useState('');
  const [transporter, setTransporter] = useState('');
  const [dispatchDate, setDispatchDate] = useState('');
  const [challanReason, setChallanReason] = useState('Supply of Goods');

  // Items
  const [items, setItems] = useState([
    { description: '', quantity: 1, rate: 0, taxRate: 18 }
  ]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await financeAPI.getSalesInvoices();
      setInvoices(res.data.data || []);
    } catch (err) {
      toast.error('Failed to load generated invoices');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleAddItem = () => {
    setItems([...items, { description: '', quantity: 1, rate: 0, taxRate: 18 }]);
  };

  const handleRemoveItem = (index) => {
    if (items.length === 1) {
      toast.error('At least one item is required');
      return;
    }
    setItems(items.filter((_, idx) => idx !== index));
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    if (field === 'quantity') {
      newItems[index].quantity = Math.max(0, parseInt(value) || 0);
    } else if (field === 'rate') {
      newItems[index].rate = Math.max(0, parseFloat(value) || 0);
    } else if (field === 'taxRate') {
      newItems[index].taxRate = parseInt(value) || 0;
    } else {
      newItems[index][field] = value;
    }
    setItems(newItems);
  };

  // Calculations
  const isTaxInvoice = documentType === 'tax_invoice';
  const isChallan = documentType === 'delivery_challan';
  const isNonGST = documentType === 'non_gst_bill' || isChallan;

  const calculateInvoice = () => {
    let subtotal = 0;
    let taxAmount = 0;
    const computedItems = items.map(item => {
      const lineSubtotal = item.quantity * item.rate;
      const lineTax = isTaxInvoice ? lineSubtotal * (item.taxRate / 100) : 0;
      const amount = lineSubtotal;
      subtotal += lineSubtotal;
      taxAmount += lineTax;
      return { ...item, taxRate: isTaxInvoice ? item.taxRate : 0, amount };
    });
    const discountAmount = Number(discount) || 0;
    const finalTaxAmount = isTaxInvoice ? taxAmount : 0;
    const grandTotal = Math.max(0, subtotal - discountAmount + finalTaxAmount);
    return { items: computedItems, subtotal, discountAmount, taxAmount: finalTaxAmount, grandTotal };
  };

  const { subtotal, discountAmount, taxAmount, grandTotal } = calculateInvoice();

  const handleCreateInvoice = async (e) => {
    e.preventDefault();
    if (!clientName.trim()) {
      toast.error('Please enter Client / Company Name');
      return;
    }

    if (items.some(item => !item.description.trim())) {
      toast.error('Please enter a description for all items');
      return;
    }

    // GST Validation logic
    if (isTaxInvoice) {
      if (!clientGST.trim()) {
        toast.error('Client GSTIN is REQUIRED for Tax Invoices!');
        return;
      }
      const gstStatus = validateGSTIN(clientGST);
      if (!gstStatus.isValid) {
        toast.error(`Invoice Blocked! ${gstStatus.message}. Please enter a valid GSTIN.`);
        return;
      }
    } else if (clientGST.trim()) {
      const gstStatus = validateGSTIN(clientGST);
      if (!gstStatus.isValid) {
        toast.error(`Invalid GSTIN format: ${gstStatus.message}. Please correct or clear the field.`);
        return;
      }
    }

    const { items: computedItems, subtotal: finalSubtotal, discountAmount: finalDiscount, taxAmount: finalTax, grandTotal: finalTotal } = calculateInvoice();

    try {
      await financeAPI.createSalesInvoice({
        documentType,
        invoiceNumber,
        challanNumber: isChallan ? invoiceNumber : undefined,
        vehicleNumber: isChallan ? vehicleNumber : undefined,
        transporter: isChallan ? transporter : undefined,
        dispatchDate: isChallan ? (dispatchDate || new Date()) : undefined,
        challanReason: isChallan ? challanReason : undefined,
        clientName,
        clientAddress,
        clientGST,
        dueDate,
        notes,
        items: computedItems,
        subtotal: finalSubtotal,
        discountAmount: finalDiscount,
        taxAmount: finalTax,
        grandTotal: finalTotal,
        isNonGST
      });

      toast.success(
        isChallan
          ? '📦 Delivery Challan generated successfully!'
          : isTaxInvoice
          ? '🧾 Sales Tax Invoice generated successfully!'
          : '📄 Standard Bill (Non-GST) generated successfully!'
      );
      setShowCreateModal(false);

      // Reset form
      setClientName('');
      setClientAddress('');
      setClientGST('');
      setInvoiceNumber('');
      setDueDate('');
      setDiscount(0);
      setNotes('');
      setVehicleNumber('');
      setTransporter('');
      setDispatchDate('');
      setChallanReason('Supply of Goods');
      setItems([{ description: '', quantity: 1, rate: 0, taxRate: 18 }]);

      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to generate document');
    }
  };

  const handleDownloadPDF = async (id, invNum) => {
    const toastId = toast.loading('Generating PDF document...');
    try {
      const response = await API.get(`/finance/sales-invoices/${id}/pdf?t=${Date.now()}`, { responseType: 'blob' });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${invNum || 'Document'}.pdf`);
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('✅ PDF downloaded successfully!', { id: toastId });
    } catch (error) {
      console.error('PDF download error:', error);
      toast.error('Failed to download PDF', { id: toastId });
    }
  };

  const handleDeleteInvoice = async (id, invNum) => {
    if (!window.confirm(`Are you sure you want to delete ${invNum}?`)) return;
    try {
      toast.loading('Deleting document...', { id: 'delete-toast' });
      await financeAPI.deleteSalesInvoice(id);
      toast.success('Document deleted successfully', { id: 'delete-toast' });
      fetchData();
    } catch (err) {
      toast.error('Failed to delete document', { id: 'delete-toast' });
    }
  };

  const filteredInvoices = useMemo(() => {
    return invoices.filter(inv => {
      // Filter tab check
      if (activeFilterTab === 'challan') {
        if (inv.documentType !== 'delivery_challan' && !inv.challanNumber) return false;
      } else if (activeFilterTab === 'non_gst') {
        if (inv.documentType !== 'non_gst_bill' && !inv.isNonGST) return false;
        if (inv.documentType === 'delivery_challan' || inv.challanNumber) return false;
      } else if (activeFilterTab === 'tax_invoice') {
        if (inv.documentType === 'delivery_challan' || inv.isNonGST || inv.documentType === 'non_gst_bill') return false;
      }

      // Search query
      const query = searchQuery.toLowerCase().trim();
      if (!query) return true;

      const invoiceNo = (inv.invoiceNumber || inv.challanNumber || '').toLowerCase();
      const clientName = (inv.clientName || '').toLowerCase();
      const vehicle = (inv.vehicleNumber || '').toLowerCase();
      const transporterName = (inv.transporter || '').toLowerCase();

      return invoiceNo.includes(query) ||
        clientName.includes(query) ||
        vehicle.includes(query) ||
        transporterName.includes(query) ||
        String(inv.grandTotal || '').includes(query);
    });
  }, [invoices, activeFilterTab, searchQuery]);

  const tdStyle = {
    padding: '12px 18px',
    fontSize: '0.86rem',
    color: 'var(--text-primary)'
  };

  const mainContent = (
    <div style={{ maxWidth: '1400px', margin: '0 auto', paddingBottom: '40px' }}>
      
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '20px',
        borderBottom: '1px solid var(--border)',
        paddingBottom: '16px',
        flexWrap: 'wrap',
        gap: '12px'
      }}>
        <div>
          <h2 style={{ color: 'var(--text-primary)', fontSize: '1.4rem', margin: 0, fontWeight: 800, display: 'flex', alignItems: 'center', gap: '8px' }}>
            <span style={{ background: 'linear-gradient(135deg, #3b82f6, #6366f1)', color: '#fff', padding: '6px 12px', borderRadius: '10px' }}>
              🧾
            </span>
            Standard Bill, Delivery Challan & Invoice Generator
          </h2>
          <p style={{ margin: '4px 0 0', color: 'var(--text-dim)', fontSize: '0.84rem' }}>
            Create Standard Non-GST Bills, Official Delivery Challans (DC), or Full Tax Invoices in a single unified workspace.
          </p>
        </div>

        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button className="btn btn-secondary btn-sm" onClick={fetchData}>
            <HiOutlineRefresh /> Reload
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setDocumentType('non_gst_bill');
              setShowCreateModal(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #10b981, #059669)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(16,185,129,0.3)'
            }}
          >
            <HiOutlinePlus style={{ strokeWidth: 3 }} /> + 📄 Standard Bill (Non-GST)
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setDocumentType('delivery_challan');
              setShowCreateModal(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #8b5cf6, #7c3aed)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(139,92,246,0.3)'
            }}
          >
            <HiOutlinePlus style={{ strokeWidth: 3 }} /> + 📦 Delivery Challan
          </button>
          <button
            className="btn btn-primary btn-sm"
            onClick={() => {
              setDocumentType('tax_invoice');
              setShowCreateModal(true);
            }}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
              fontWeight: 700,
              background: 'linear-gradient(135deg, #3b82f6, #2563eb)',
              border: 'none',
              boxShadow: '0 4px 12px rgba(59,130,246,0.3)'
            }}
          >
            <HiOutlinePlus style={{ strokeWidth: 3 }} /> + 🧾 Tax Invoice (GST)
          </button>
        </div>
      </div>

        {/* Filter Pills & Search */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px',
          flexWrap: 'wrap',
          gap: '12px'
        }}>
          {/* Filter Pills */}
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            <button
              className={`btn btn-xs ${activeFilterTab === 'all' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveFilterTab('all')}
              style={{ borderRadius: '20px', padding: '6px 14px', fontWeight: 600 }}
            >
              All Documents ({invoices.length})
            </button>

            <button
              className={`btn btn-xs ${activeFilterTab === 'non_gst' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveFilterTab('non_gst')}
              style={{
                borderRadius: '20px',
                padding: '6px 14px',
                fontWeight: 600,
                borderColor: activeFilterTab === 'non_gst' ? '#10b981' : undefined
              }}
            >
              📄 Standard Bills (Non-GST)
            </button>

            <button
              className={`btn btn-xs ${activeFilterTab === 'challan' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveFilterTab('challan')}
              style={{
                borderRadius: '20px',
                padding: '6px 14px',
                fontWeight: 600,
                borderColor: activeFilterTab === 'challan' ? '#8b5cf6' : undefined
              }}
            >
              📦 Delivery Challans (DC)
            </button>

            <button
              className={`btn btn-xs ${activeFilterTab === 'tax_invoice' ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => setActiveFilterTab('tax_invoice')}
              style={{
                borderRadius: '20px',
                padding: '6px 14px',
                fontWeight: 600,
                borderColor: activeFilterTab === 'tax_invoice' ? '#3b82f6' : undefined
              }}
            >
              🧾 Tax Invoices (GST)
            </button>
          </div>

          {/* Search Input Bar */}
          <div className="form-input-icon" style={{ width: '320px' }}>
            <HiOutlineSearch className="icon" />
            <input
              className="form-input"
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              placeholder="Search by client, invoice/DC#, vehicle..."
              style={{ padding: '8px 12px 8px 36px', fontSize: '0.85rem' }}
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery('')}
                style={{ position: 'absolute', right: '10px', top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-dim)', cursor: 'pointer' }}
              >
                <HiOutlineX size={14} />
              </button>
            )}
          </div>
        </div>

        {/* Invoices Directory Table */}
        {loading ? (
          <div style={{ textAlign: 'center', padding: '60px', color: 'var(--text-dim)' }}>
            <div className="spinner" style={{ margin: '0 auto 12px' }} /> Loading documents...
          </div>
        ) : (
          <div className="azure-card" style={{ padding: 0, overflow: 'hidden' }}>
            <div className="azure-table-container">
              <table className="azure-table">
                <thead>
                  <tr>
                    <th>Doc # & Type</th>
                    <th>Client / Consignee</th>
                    <th>Date</th>
                    <th>Transport / Vehicle</th>
                    <th>Tax Amount</th>
                    <th>Grand Total</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredInvoices.length === 0 ? (
                    <tr>
                      <td colSpan={7} style={{ padding: '40px', textAlign: 'center', color: 'var(--text-dim)' }}>
                        {searchQuery ? 'No documents match your search criteria.' : 'No invoices or delivery challans created yet. Click "+ Create Bill / Delivery Challan" to start!'}
                      </td>
                    </tr>
                  ) : (
                    filteredInvoices.map(inv => {
                      const isChallanDoc = inv.documentType === 'delivery_challan' || Boolean(inv.challanNumber);
                      const isNonGSTDoc = (inv.documentType === 'non_gst_bill' || inv.isNonGST) && !isChallanDoc;

                      return (
                        <tr key={inv._id}>
                          <td style={tdStyle}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <span style={{ fontWeight: 700, color: 'var(--primary)' }}>
                                {inv.challanNumber || inv.invoiceNumber}
                              </span>
                              {isChallanDoc ? (
                                <span style={{
                                  fontSize: '0.7rem',
                                  background: 'rgba(139, 92, 246, 0.15)',
                                  color: '#8b5cf6',
                                  border: '1px solid rgba(139, 92, 246, 0.3)',
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  fontWeight: 700
                                }}>
                                  📦 Delivery Challan
                                </span>
                              ) : isNonGSTDoc ? (
                                <span style={{
                                  fontSize: '0.7rem',
                                  background: 'rgba(16, 185, 129, 0.15)',
                                  color: '#10b981',
                                  border: '1px solid rgba(16, 185, 129, 0.3)',
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  fontWeight: 700
                                }}>
                                  📄 Non-GST Bill
                                </span>
                              ) : (
                                <span style={{
                                  fontSize: '0.7rem',
                                  background: 'rgba(59, 130, 246, 0.15)',
                                  color: '#3b82f6',
                                  border: '1px solid rgba(59, 130, 246, 0.3)',
                                  padding: '2px 8px',
                                  borderRadius: '6px',
                                  fontWeight: 700
                                }}>
                                  🧾 Tax Invoice
                                </span>
                              )}
                            </div>
                          </td>

                          <td style={tdStyle}>
                            <div style={{ fontWeight: 700, color: 'var(--text-primary)' }}>{inv.clientName}</div>
                            {inv.clientGST && (
                              <div style={{ fontSize: '0.74rem', color: 'var(--text-dim)' }}>GST: {inv.clientGST}</div>
                            )}
                          </td>

                          <td style={{ ...tdStyle, fontSize: '0.8rem', color: 'var(--text-dim)' }}>
                            {new Date(inv.invoiceDate || inv.createdAt).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                          </td>

                          <td style={tdStyle}>
                            {isChallanDoc ? (
                              <div>
                                <div style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                                  🚚 {inv.vehicleNumber || 'Vehicle Assigned'}
                                </div>
                                <div style={{ fontSize: '0.74rem', color: 'var(--text-dim)' }}>
                                  {inv.transporter || inv.challanReason || 'Goods Transfer'}
                                </div>
                              </div>
                            ) : (
                              <span style={{ color: 'var(--text-dim)', fontSize: '0.78rem' }}>--</span>
                            )}
                          </td>

                          <td style={tdStyle}>
                            {isChallanDoc || isNonGSTDoc ? (
                              <span style={{ color: '#10b981', fontWeight: 600, fontSize: '0.8rem' }}>₹0.00 (Exempt)</span>
                            ) : (
                              `₹${(inv.taxAmount || 0).toFixed(2)}`
                            )}
                          </td>

                          <td style={{ ...tdStyle, color: 'var(--primary)', fontWeight: 800, fontSize: '0.95rem' }}>
                            ₹{(inv.grandTotal || 0).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                          </td>

                          <td style={tdStyle}>
                            <div style={{ display: 'flex', gap: '8px' }}>
                              <button
                                onClick={() => handleDownloadPDF(inv._id, inv.challanNumber || inv.invoiceNumber)}
                                className="btn btn-secondary btn-xs"
                                style={{ display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 600 }}
                                title="Download Official PDF"
                              >
                                <HiOutlineDownload /> PDF
                              </button>
                              <button
                                onClick={() => handleDeleteInvoice(inv._id, inv.challanNumber || inv.invoiceNumber)}
                                className="btn btn-danger btn-xs"
                                title="Delete Document"
                              >
                                <HiOutlineTrash />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* ========================================================================= */}
        {/* UNIFIED CREATION MODAL WITH SMOOTH MODE SWITCHER                          */}
        {/* ========================================================================= */}
        {showCreateModal && (
          <div className="modal-overlay" style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', backdropFilter: 'blur(6px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '20px'
          }}>
            <div className="azure-card" style={{
              background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: '18px',
              width: '100%', maxWidth: '850px', maxHeight: '92vh', display: 'flex', flexDirection: 'column',
              overflow: 'hidden', boxShadow: '0 20px 60px rgba(0,0,0,0.5)'
            }}>
              
              {/* Modal Header */}
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '20px 28px', borderBottom: '1px solid var(--border)', flexShrink: 0 }}>
                <div>
                  <h3 style={{ fontSize: '1.25rem', fontWeight: 800, color: 'var(--text-primary)', margin: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {documentType === 'delivery_challan' ? (
                      <>📦 Generate Official Delivery Challan (DC)</>
                    ) : documentType === 'non_gst_bill' ? (
                      <>📄 Generate Standard Bill (Non-GST)</>
                    ) : (
                      <>🧾 Generate Professional Tax Invoice (GST)</>
                    )}
                  </h3>
                  <div style={{ fontSize: '0.78rem', color: 'var(--text-dim)', marginTop: '2px' }}>
                    Select the document format below and fill in the details.
                  </div>
                </div>
                <button
                  onClick={() => setShowCreateModal(false)}
                  style={{ background: 'none', border: 'none', color: 'var(--text-dim)', fontSize: '1.3rem', cursor: 'pointer' }}
                >
                  <HiOutlineX />
                </button>
              </div>

              {/* Modal Body */}
              <div style={{ padding: '24px 28px', overflowY: 'auto', overflowX: 'hidden', flex: 1, display: 'flex', flexDirection: 'column', gap: '18px' }}>
                {/* 🔄 UNIFIED 3-WAY DOCUMENT TYPE SWITCHER */}
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(3, 1fr)',
                    gap: '8px',
                    background: 'rgba(255,255,255,0.02)',
                    padding: '6px',
                    borderRadius: '12px',
                    border: '1px solid var(--border)'
                  }}>
                <button
                  type="button"
                  onClick={() => setDocumentType('non_gst_bill')}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: documentType === 'non_gst_bill' ? '2px solid #10b981' : '1px solid transparent',
                    background: documentType === 'non_gst_bill' ? 'rgba(16, 185, 129, 0.15)' : 'transparent',
                    color: documentType === 'non_gst_bill' ? '#10b981' : 'var(--text-dim)',
                    fontWeight: 700,
                    fontSize: '0.84rem',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <span style={{ fontSize: '1.1rem' }}>📄</span>
                  <span>Standard Bill (Non-GST)</span>
                  <span style={{ fontSize: '0.68rem', fontWeight: 400, opacity: 0.8 }}>0% GST / Retail & Non-GST</span>
                </button>

                <button
                  type="button"
                  onClick={() => setDocumentType('delivery_challan')}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: documentType === 'delivery_challan' ? '2px solid #8b5cf6' : '1px solid transparent',
                    background: documentType === 'delivery_challan' ? 'rgba(139, 92, 246, 0.15)' : 'transparent',
                    color: documentType === 'delivery_challan' ? '#8b5cf6' : 'var(--text-dim)',
                    fontWeight: 700,
                    fontSize: '0.84rem',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <span style={{ fontSize: '1.1rem' }}>📦</span>
                  <span>Delivery Challan (DC)</span>
                  <span style={{ fontSize: '0.68rem', fontWeight: 400, opacity: 0.8 }}>Goods Movement / Vehicle No</span>
                </button>

                <button
                  type="button"
                  onClick={() => setDocumentType('tax_invoice')}
                  style={{
                    padding: '10px 12px',
                    borderRadius: '10px',
                    border: documentType === 'tax_invoice' ? '2px solid #3b82f6' : '1px solid transparent',
                    background: documentType === 'tax_invoice' ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                    color: documentType === 'tax_invoice' ? '#3b82f6' : 'var(--text-dim)',
                    fontWeight: 700,
                    fontSize: '0.84rem',
                    cursor: 'pointer',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: '4px',
                    transition: 'all 0.15s ease'
                  }}
                >
                  <span style={{ fontSize: '1.1rem' }}>🧾</span>
                  <span>Tax Invoice (GST)</span>
                  <span style={{ fontSize: '0.68rem', fontWeight: 400, opacity: 0.8 }}>GSTIN Required (18% / 28%)</span>
                </button>
              </div>

              {/* Mode Banner Description */}
              <div style={{
                padding: '10px 14px',
                borderRadius: '10px',
                background: documentType === 'delivery_challan' ? 'rgba(139, 92, 246, 0.08)' : documentType === 'non_gst_bill' ? 'rgba(16, 185, 129, 0.08)' : 'rgba(59, 130, 246, 0.08)',
                border: `1px solid ${documentType === 'delivery_challan' ? 'rgba(139, 92, 246, 0.25)' : documentType === 'non_gst_bill' ? 'rgba(16, 185, 129, 0.25)' : 'rgba(59, 130, 246, 0.25)'}`,
                fontSize: '0.8rem',
                color: 'var(--text-primary)'
              }}>
                {documentType === 'delivery_challan' ? (
                  <><strong>📦 Delivery Challan Mode:</strong> Creates an official Goods Delivery Note. GSTIN is optional. Vehicle number, transporter and purpose of transport will appear on the printed document.</>
                ) : documentType === 'non_gst_bill' ? (
                  <><strong>📄 Standard Bill Mode:</strong> 0% GST applied. Perfect for regular non-GST sales, retail clients, or billing without tax liability.</>
                ) : (
                  <><strong>🧾 Tax Invoice Mode:</strong> Standard GST rates applied. Client GST Number is <strong>Required</strong> with official checksum validation.</>
                )}
              </div>

              <form onSubmit={handleCreateInvoice} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {/* Row 1 - Client Info */}
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" style={{ fontWeight: 600 }}>
                      {documentType === 'delivery_challan' ? 'CONSIGNEE / CLIENT NAME *' : 'CLIENT / COMPANY NAME *'}
                    </label>
                    <input
                      className="form-input"
                      required
                      value={clientName}
                      onChange={e => setClientName(e.target.value)}
                      placeholder="e.g. Acme Industries Ltd"
                    />
                  </div>

                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" style={{ fontWeight: 600 }}>
                      CLIENT GSTIN {isTaxInvoice ? <span style={{ color: '#ef4444' }}>* (REQUIRED)</span> : <span style={{ color: '#10b981' }}>(OPTIONAL)</span>}
                    </label>
                    <input 
                      className="form-input" 
                      required={isTaxInvoice}
                      value={clientGST} 
                      onChange={e => setClientGST(e.target.value.toUpperCase())} 
                      placeholder={isTaxInvoice ? "e.g. 09AAACH7409R1ZZ (Required)" : "e.g. 09AAACH7409R1ZZ (Optional)"}
                      maxLength={15}
                      style={clientGST.trim() ? {
                        borderColor: validateGSTIN(clientGST).isValid ? '#10b981' : '#ef4444',
                        boxShadow: validateGSTIN(clientGST).isValid ? '0 0 0 2px rgba(16,185,129,0.2)' : '0 0 0 2px rgba(239,68,68,0.25)'
                      } : (isTaxInvoice && !clientGST.trim() ? { borderColor: 'rgba(239,68,68,0.5)' } : {})}
                    />
                    {clientGST && (
                      <div style={{
                        fontSize: '0.74rem', marginTop: '4px', fontWeight: 600,
                        color: validateGSTIN(clientGST).isValid ? '#10b981' : (clientGST.length === 15 ? '#ef4444' : '#f59e0b')
                      }}>
                        {validateGSTIN(clientGST).message}
                      </div>
                    )}
                  </div>
                </div>

                {/* Delivery Challan Specific Fields (Vehicle, Transporter, Purpose) */}
                {isChallan && (
                  <div style={{
                    padding: '14px',
                    borderRadius: '12px',
                    background: 'rgba(139, 92, 246, 0.05)',
                    border: '1px dashed rgba(139, 92, 246, 0.3)',
                    display: 'grid',
                    gridTemplateColumns: '1fr 1fr 1fr',
                    gap: '14px'
                  }}>
                    <div>
                      <label className="form-label" style={{ color: '#8b5cf6', fontWeight: 700 }}>Vehicle Number</label>
                      <input
                        className="form-input"
                        placeholder="e.g. MH-12-AB-1234"
                        value={vehicleNumber}
                        onChange={e => setVehicleNumber(e.target.value.toUpperCase())}
                      />
                    </div>

                    <div>
                      <label className="form-label" style={{ color: '#8b5cf6', fontWeight: 700 }}>Transporter Name</label>
                      <input
                        className="form-input"
                        placeholder="e.g. VRL Logistics / Own Tempo"
                        value={transporter}
                        onChange={e => setTransporter(e.target.value)}
                      />
                    </div>

                    <div>
                      <label className="form-label" style={{ color: '#8b5cf6', fontWeight: 700 }}>Purpose of Transport</label>
                      <select
                        className="form-input"
                        value={challanReason}
                        onChange={e => setChallanReason(e.target.value)}
                      >
                        {CHALLAN_PURPOSES.map(p => (
                          <option key={p} value={p}>{p}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                )}

                {/* Row 2 - Address & Dates */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '16px' }}>
                  <div className="form-group" style={{ margin: 0 }}>
                    <label className="form-label" style={{ fontWeight: 600 }}>
                      {isChallan ? 'DELIVERY / DISPATCH ADDRESS' : 'CLIENT BILLING ADDRESS'}
                    </label>
                    <textarea
                      className="form-input"
                      style={{ minHeight: '60px', resize: 'vertical' }}
                      value={clientAddress}
                      onChange={e => setClientAddress(e.target.value)}
                      placeholder="Full Destination Address"
                    />
                  </div>

                  <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontWeight: 600 }}>
                        {isChallan ? 'CHALLAN NUMBER (AUTO/CUSTOM)' : 'INVOICE NUMBER (AUTO/CUSTOM)'}
                      </label>
                      <input
                        className="form-input"
                        value={invoiceNumber}
                        onChange={e => setInvoiceNumber(e.target.value)}
                        placeholder={isChallan ? 'e.g. DC-2026-0001 (Leave blank to auto-generate)' : 'e.g. INV-2026-0001 (Leave blank to auto-generate)'}
                      />
                    </div>

                    <div className="form-group" style={{ margin: 0 }}>
                      <label className="form-label" style={{ fontWeight: 600 }}>
                        {isChallan ? 'DISPATCH DATE' : 'DUE DATE'}
                      </label>
                      <input
                        type="date"
                        className="form-input"
                        value={isChallan ? dispatchDate : dueDate}
                        onChange={e => isChallan ? setDispatchDate(e.target.value) : setDueDate(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Line Items Table */}
                <div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
                    <label className="form-label" style={{ fontWeight: 700, margin: 0 }}>
                      📦 ORDERED ITEMS & QUANTITIES
                    </label>
                    <button
                      type="button"
                      onClick={handleAddItem}
                      className="btn btn-secondary btn-xs"
                      style={{ display: 'flex', alignItems: 'center', gap: '4px', color: 'var(--primary)' }}
                    >
                      <HiOutlinePlus /> Add Item
                    </button>
                  </div>

                  <div style={{ border: '1px solid var(--border)', borderRadius: '10px', overflow: 'hidden' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.82rem' }}>
                      <thead>
                        <tr style={{ background: 'rgba(255,255,255,0.03)', borderBottom: '1px solid var(--border)' }}>
                          <th style={{ padding: '8px 10px', textAlign: 'left' }}>Description *</th>
                          <th style={{ padding: '8px 10px', width: '75px', textAlign: 'right' }}>Qty</th>
                          <th style={{ padding: '8px 10px', width: '100px', textAlign: 'right' }}>Rate (₹)</th>
                          {isTaxInvoice && <th style={{ padding: '8px 10px', width: '85px', textAlign: 'right' }}>GST %</th>}
                          <th style={{ padding: '8px 10px', width: '110px', textAlign: 'right' }}>Total (₹)</th>
                          <th style={{ padding: '8px 10px', width: '36px' }}></th>
                        </tr>
                      </thead>
                      <tbody>
                        {items.map((item, idx) => (
                          <tr key={idx} style={{ borderBottom: '1px solid var(--border)' }}>
                            <td style={{ padding: '6px 8px' }}>
                              <input
                                className="form-input"
                                style={{ padding: '6px 8px', fontSize: '0.82rem' }}
                                required
                                value={item.description}
                                onChange={e => handleItemChange(idx, 'description', e.target.value)}
                                placeholder="e.g. C.36 Nylon Drag Chain with End Brackets"
                              />
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <input
                                type="number"
                                min="1"
                                className="form-input"
                                style={{ padding: '6px 8px', textAlign: 'right', fontSize: '0.82rem' }}
                                value={item.quantity}
                                onChange={e => handleItemChange(idx, 'quantity', e.target.value)}
                              />
                            </td>
                            <td style={{ padding: '6px 8px' }}>
                              <input
                                type="number"
                                min="0"
                                step="any"
                                className="form-input"
                                style={{ padding: '6px 8px', textAlign: 'right', fontSize: '0.82rem' }}
                                value={item.rate}
                                onChange={e => handleItemChange(idx, 'rate', e.target.value)}
                              />
                            </td>
                            {isTaxInvoice && (
                              <td style={{ padding: '6px 8px' }}>
                                <select
                                  className="form-input"
                                  style={{ padding: '6px 4px', textAlign: 'right', fontSize: '0.82rem' }}
                                  value={item.taxRate}
                                  onChange={e => handleItemChange(idx, 'taxRate', e.target.value)}
                                >
                                  <option value="18">18%</option>
                                  <option value="28">28%</option>
                                  <option value="12">12%</option>
                                  <option value="5">5%</option>
                                  <option value="0">0%</option>
                                </select>
                              </td>
                            )}
                            <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 700, color: 'var(--text-primary)' }}>
                              ₹{(item.quantity * item.rate).toLocaleString('en-IN', { minimumFractionDigits: 2 })}
                            </td>
                            <td style={{ padding: '6px 4px', textAlign: 'center' }}>
                              <button
                                type="button"
                                onClick={() => handleRemoveItem(idx)}
                                style={{ background: 'none', border: 'none', color: '#ef4444', cursor: 'pointer' }}
                              >
                                <HiOutlineTrash size={16} />
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Summary & Totals */}
                <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 0.8fr', gap: '20px', alignItems: 'flex-start' }}>
                  <div>
                    <label className="form-label" style={{ fontWeight: 600 }}>NOTES & INSTRUCTIONS</label>
                    <textarea
                      className="form-input"
                      style={{ minHeight: '65px', resize: 'vertical' }}
                      value={notes}
                      onChange={e => setNotes(e.target.value)}
                      placeholder={isChallan ? "e.g. Please verify carton seal on delivery. Handover POD upon receipt." : "e.g. Payment due within 30 days. Thank you for your business!"}
                    />
                  </div>

                  <div style={{
                    padding: '14px 16px',
                    borderRadius: '10px',
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid var(--border)',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '6px'
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span style={{ color: 'var(--text-dim)' }}>Subtotal:</span>
                      <span style={{ fontWeight: 600 }}>₹{subtotal.toFixed(2)}</span>
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '0.85rem' }}>
                      <span style={{ color: 'var(--text-dim)' }}>Discount (₹):</span>
                      <input
                        type="number"
                        min="0"
                        className="form-input"
                        style={{ width: '90px', padding: '2px 6px', textAlign: 'right', height: '26px' }}
                        value={discount}
                        onChange={e => setDiscount(e.target.value)}
                      />
                    </div>

                    <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '0.85rem' }}>
                      <span style={{ color: 'var(--text-dim)' }}>GST Tax:</span>
                      <span style={{ fontWeight: 600, color: isTaxInvoice ? '#3b82f6' : '#10b981' }}>
                        {isTaxInvoice ? `₹${taxAmount.toFixed(2)}` : '₹0.00 (Exempt)'}
                      </span>
                    </div>

                    <div style={{
                      display: 'flex',
                      justifyContent: 'space-between',
                      borderTop: '1px solid var(--border)',
                      paddingTop: '8px',
                      marginTop: '4px',
                      fontWeight: 800,
                      fontSize: '1.05rem',
                      color: 'var(--primary)'
                    }}>
                      <span>Total Amount:</span>
                      <span>₹{grandTotal.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                    </div>
                  </div>
                </div>

                {/* Modal Buttons */}
                <div style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '10px',
                  borderTop: '1px solid var(--border)',
                  paddingTop: '16px',
                  marginTop: '8px'
                }}>
                  <button
                    type="button"
                    className="btn btn-secondary btn-sm"
                    onClick={() => setShowCreateModal(false)}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="btn btn-primary btn-sm"
                    style={{
                      fontWeight: 800,
                      background: documentType === 'delivery_challan'
                        ? 'linear-gradient(135deg, #8b5cf6, #7c3aed)'
                        : documentType === 'non_gst_bill'
                        ? 'linear-gradient(135deg, #10b981, #059669)'
                        : 'linear-gradient(135deg, #3b82f6, #2563eb)',
                      border: 'none',
                      padding: '8px 22px'
                    }}
                  >
                    {documentType === 'delivery_challan'
                      ? '📦 Generate Delivery Challan (DC)'
                      : documentType === 'non_gst_bill'
                      ? '📄 Generate Standard Bill'
                      : '🧾 Generate Tax Invoice'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  if (isEmbedded) {
    return mainContent;
  }

  return (
    <DashboardLayout pageTitle="Bill & Challan Generator">
      {mainContent}
    </DashboardLayout>
  );
}
